import { DialogType } from "modules/dialogNotification/constants";
import { useContext, useMemo } from "react";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import { selectUserId } from "modules/auth/redux/selectors";
import { selectCurrentEventId } from "modules/event/selectors";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import {
  addDialogNotification,
  closeDialogNotificationsByType,
  updateDialogNotificationParams,
} from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";
import { useStyles } from "./styles";
import { setActivityStatus } from "../../firebase";
import { ActivityStatus } from "../../types";
import { SetActivityStatusDialog } from "../SetActivityStatusDialog";
import { ActivityStatusIndicator } from "../ActivityStatusIndicator";
import { selectCurrentUserActivityStatus } from "../../redux/selectors";

export const UserProfileMenuStatus = () => {
  const { track } = useContext(TRACKING_CONTEXT);
  const dispatch = useAppDispatch();
  const userId = useSelector(selectUserId);
  const currentEventId = useSelector(selectCurrentEventId);
  const currentUserActivityStatus = useSelector(
    selectCurrentUserActivityStatus,
  );
  const classes = useStyles({
    status: currentUserActivityStatus?.status ?? null,
  });
  const { t } = useI18n(["event", "common"]);

  const activityStatus = useMemo(
    () =>
      currentUserActivityStatus ?? {
        status: ActivityStatus.ACTIVE,
        label: ActivityStatus.ACTIVE,
      },
    [currentUserActivityStatus],
  );

  const handleClose = () => {
    dispatch(closeDialogNotificationsByType(DialogType.SET_ACTIVITY_STATUS));
  };

  const handleStatusChange = (status: ActivityStatus, label: string) => {
    if (!currentEventId || !userId) {
      return;
    }

    setActivityStatus(currentEventId, userId, {
      status,
      label: label === "" ? status : label,
    });
    track(Events.EVENT_ACTIVITY_STATUS_CHANGED, {
      status,
      hasCustomStatusMessage: `${label !== ""}`,
    });
    dispatch(closeDialogNotificationsByType(DialogType.SET_ACTIVITY_STATUS));
  };

  const handleChange = (status: ActivityStatus, label: string) => {
    dispatch(
      updateDialogNotificationParams({
        type: DialogType.SET_ACTIVITY_STATUS,
        data: {
          content: (
            <SetActivityStatusDialog
              status={status}
              label={label === status.toLowerCase() ? "" : label}
              onChange={handleChange}
            />
          ),
          onConfirm: () => handleStatusChange(status, label),
        },
      }),
    );
  };

  const handleClick = () => {
    dispatch(
      addDialogNotification({
        confirmTestId: "toggle-confirm",
        dismissTestId: "toggle-dismiss",
        contentClassName: classes.dialogContent,
        content: (
          <SetActivityStatusDialog
            status={activityStatus.status}
            label={
              activityStatus.label === activityStatus.status.toLowerCase()
                ? ""
                : activityStatus.label
            }
            onChange={handleChange}
          />
        ),
        onDismiss: handleClose,
        onConfirm: () =>
          handleStatusChange(activityStatus.status, activityStatus.label),
        confirmVariant: "secondary",
        confirmText: t("common:set"),
        dismissText: t("common:button.cancel"),
        actionsClassName: classes.dialogActions,
        type: DialogType.SET_ACTIVITY_STATUS,
        title: t("event:set.your.status"),
      }),
    );
  };

  if (!currentEventId || !userId) {
    return null;
  }

  return (
    <div className={classes.container}>
      <ActivityStatusIndicator userId={userId} onClick={handleClick} />
      <Typography
        variant="body1"
        className={classes.changeButton}
        onClick={handleClick}
      >
        {t("common:button.change")}
      </Typography>
    </div>
  );
};
